import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { ChevronDown, ChevronRight, Fingerprint, Info, Loader2, Lock } from 'lucide-react';
import type {
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister
} from 'react-hook-form';
import type { MultiMethodLoginFormData } from './types';

interface UiConfig {
  headerTitle: string;
  headerSubtitle: string;
  headerSpacing: string;
  inputHeight: string;
  primaryBtnHeight: string;
  sectionGap: string;
  altBtnHeight: string;
}

interface EmailStepProps {
  ui: UiConfig;
  error: string | null;
  infoMessage: string | null;
  register: UseFormRegister<MultiMethodLoginFormData>;
  errors: FieldErrors<MultiMethodLoginFormData>;
  handleSubmit: UseFormHandleSubmit<MultiMethodLoginFormData>;
  onContinue: (data: MultiMethodLoginFormData) => void;
  isEmailValid: boolean;
  isLoading: boolean;
  isSendingOtp: boolean;
  passkeySupported: boolean;
  isPasskeyLoading: boolean;
  onPasskeyLogin: () => void;
  onChoosePassword: () => void;
  autoFocus?: boolean;
  /** Hide the form header when social buttons are shown above */
  showHeader?: boolean;
}

export const EmailStep: React.FC<EmailStepProps> = ({
  ui: _ui,
  error,
  infoMessage,
  register,
  errors,
  handleSubmit,
  onContinue,
  isEmailValid,
  isLoading,
  isSendingOtp,
  passkeySupported,
  isPasskeyLoading,
  onPasskeyLogin,
  onChoosePassword,
  autoFocus,
  showHeader = true
}) => {
  const { t } = useTranslation('auth');
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <div className="py-2">
      {/* Form Header -- hidden when social buttons are above (overlay mode) */}
      {showHeader && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">
            {t('authSheet.signInOrCreate', 'Sign in or create an account')}
          </h2>
          <p className="mt-1.5 text-sm text-gray-500">
            {t('authSheet.enterEmailToGetStarted', 'Enter your email to get started')}
          </p>
        </div>
      )}

      {/* Error/Info Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6"
          >
            <div className="rounded-lg bg-red-50 px-4 py-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </motion.div>
        )}

        {infoMessage && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6"
          >
            <div className="flex items-start gap-3 rounded-lg bg-amber-50 px-4 py-3">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-700">{infoMessage}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={handleSubmit(onContinue)} className="space-y-6">
        {/* Email Field - Clean minimal style */}
        <div className="space-y-3">
          <label
            htmlFor="email"
            className="block text-xs font-medium uppercase tracking-wider text-gray-500"
          >
            {t('multiMethod.email')}
          </label>
          <input
            {...register('email')}
            id="email"
            type="email"
            placeholder={t('multiMethod.emailPlaceholder')}
            autoFocus={autoFocus}
            autoComplete="email webauthn"
            className={
              'h-12 w-full border-0 border-b-2 bg-transparent px-0 text-base transition-colors duration-200 ' +
              'text-gray-900 placeholder:text-gray-400 ' +
              'focus:outline-hidden focus:ring-0 ' +
              (errors.email
                ? 'border-red-300 focus:border-red-500'
                : 'border-gray-200 focus:border-gray-900')
            }
          />
          {errors.email && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-xs text-red-500"
            >
              {errors.email.message}
            </motion.p>
          )}
        </div>

        {/* Primary CTA - Dark button */}
        <motion.button
          type="submit"
          disabled={!isEmailValid || isLoading}
          whileHover={isEmailValid && !isLoading ? { scale: 1.01 } : {}}
          whileTap={isEmailValid && !isLoading ? { scale: 0.99 } : {}}
          className={
            'h-12 w-full rounded-xl text-sm font-medium tracking-wide transition-all duration-200 ' +
            'flex items-center justify-center gap-2 ' +
            (isEmailValid && !isLoading
              ? 'bg-gray-900 text-white hover:bg-gray-800'
              : 'cursor-not-allowed bg-gray-100 text-gray-400')
          }
        >
          {isSendingOtp ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <span>{t('multiMethod.continueWithOtp')}</span>
              <ChevronRight className="h-4 w-4" />
            </>
          )}
        </motion.button>
      </form>

      {/* Collapsed: More sign-in options (Passkey + Password) */}
      <div className="mt-6">
        <button
          type="button"
          onClick={() => setMoreOpen(v => !v)}
          className="flex w-full items-center justify-center gap-1.5 py-2 text-xs font-medium text-gray-400 transition-colors hover:text-gray-600"
        >
          <span>{t('multiMethod.moreSignInOptions', 'More sign-in options')}</span>
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`}
          />
        </button>

        <AnimatePresence>
          {moreOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="overflow-hidden"
            >
              <div className="space-y-2 pt-2">
                {passkeySupported && (
                  <motion.button
                    type="button"
                    onClick={onPasskeyLogin}
                    disabled={isLoading}
                    whileHover={{ scale: 1.005 }}
                    whileTap={{ scale: 0.995 }}
                    className="flex h-11 w-full items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isPasskeyLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Fingerprint className="h-4 w-4 text-gray-400" />
                        <span>{t('multiMethod.passkey')}</span>
                      </>
                    )}
                  </motion.button>
                )}

                <motion.button
                  type="button"
                  onClick={onChoosePassword}
                  disabled={isLoading}
                  whileHover={{ scale: 1.005 }}
                  whileTap={{ scale: 0.995 }}
                  className="flex h-11 w-full items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 transition-all duration-200 hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Lock className="h-4 w-4 text-gray-400" />
                  <span>{t('multiMethod.password')}</span>
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
